import React, { useEffect, useState } from 'react'
import { MessageSquare, Send, TrendingUp, Activity, Bell, Users } from 'lucide-react'
import { getCommunicationStats, getSMSLogs, CommunicationStats } from '../../services/communicationsService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'

interface CommunicationsDashboardProps {
  churchId: string
}

const CommunicationsDashboard: React.FC<CommunicationsDashboardProps> = ({ churchId }) => {
  const [stats, setStats] = useState<CommunicationStats | null>(null)
  const [recentSMS, setRecentSMS] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadDashboard()
  }, [churchId])

  const loadDashboard = async () => {
    try {
      setLoading(true)
      setError(null)
      const [statsData, smsLogs] = await Promise.all([
        getCommunicationStats(churchId),
        getSMSLogs(10)
      ])
      setStats(statsData)
      setRecentSMS(smsLogs || [])

      // Show info if SMS table doesn't exist
      if (statsData.total_sms_sent === 0 && smsLogs.length === 0) {
        console.info('SMS logging table may not be created yet. SMS stats will be available after running migration 010_sms_integration.sql')
      }
    } catch (error: any) {
      console.error('Failed to load dashboard:', error)
      setError(error.message || 'Unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="pt-6">
              <div className="h-12 bg-muted rounded mb-4"></div>
              <div className="h-8 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error || !stats) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-destructive font-medium mb-2">Failed to load dashboard</p>
          {error && <p className="text-sm text-muted-foreground">{error}</p>}
          <Button onClick={loadDashboard} className="mt-4" variant="outline" size="sm">
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Announcements */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Announcements</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_announcements}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        {/* Active Announcements */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Currently Active</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active_announcements}</div>
            <p className="text-xs text-muted-foreground">Active now</p>
          </CardContent>
        </Card>

        {/* SMS Sent */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">SMS Sent</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_sms_sent}</div>
            <p className="text-xs text-muted-foreground">Total messages</p>
          </CardContent>
        </Card>

        {/* SMS Success Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">SMS Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.sms_success_rate.toFixed(0)}%</div>
            <p className="text-xs text-muted-foreground">Delivery rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Announcements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Recent Announcements
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recent_activity.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent announcements</p>
            ) : (
              <div className="space-y-3">
                {stats.recent_activity.map((activity: any) => (
                  <div
                    key={activity.id}
                    className="flex flex-col gap-1 p-3 rounded-md border bg-card hover:bg-accent transition-colors"
                  >
                    <div className="font-medium text-sm">{activity.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(activity.created_at).toLocaleDateString()} at{' '}
                      {new Date(activity.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent SMS */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Recent SMS
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentSMS.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent SMS messages</p>
            ) : (
              <div className="space-y-3">
                {recentSMS.map((sms: any) => (
                  <div
                    key={sms.id}
                    className="flex flex-col gap-2 p-3 rounded-md border bg-card"
                  >
                    <div className="flex items-start justify-between">
                      <div className="font-medium text-sm">{sms.to_phone}</div>
                      <Badge
                        variant={
                          sms.status === 'sent'
                            ? 'default'
                            : sms.status === 'failed'
                            ? 'destructive'
                            : 'secondary'
                        }
                      >
                        {sms.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground line-clamp-2">{sms.message}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(sms.sent_at).toLocaleDateString()} at{' '}
                      {new Date(sms.sent_at).toLocaleTimeString()}
                    </div>
                    {sms.error_message && (
                      <div className="text-xs text-destructive">Error: {sms.error_message}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Communication Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="text-sm font-medium">Engagement Rate</div>
              <div className="flex items-center gap-2">
                <Progress
                  value={stats.active_announcements / Math.max(stats.total_announcements, 1) * 100}
                  className="flex-1"
                />
                <span className="text-sm font-medium min-w-[3ch]">
                  {((stats.active_announcements / Math.max(stats.total_announcements, 1)) * 100).toFixed(0)}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Active vs total announcements</p>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">SMS Delivery Rate</div>
              <div className="flex items-center gap-2">
                <Progress
                  value={stats.sms_success_rate}
                  className="flex-1"
                />
                <span className="text-sm font-medium min-w-[3ch]">
                  {stats.sms_success_rate.toFixed(0)}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Successful deliveries</p>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">Total SMS Cost</div>
              <div className="text-2xl font-bold">
                GHS {(stats.total_sms_sent * 0.035).toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.total_sms_sent} messages sent
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default CommunicationsDashboard
